import { applyPerimeterThreshold, clearPerimeterThreshold, setPerimeterRule, setPerimeterFrozen, setBucket, setBucketBulk, setDay2Exit, setJoinStage, setStrandedPlan, } from './decisions.js';
import { addBuildUp, addEntity, addTsa, deleteBuildUp, deleteEntity, deleteTsa, remapEntity, updateBuildUp, updateEntity, updateTsa, } from './registers.js';
import { createScenario, deleteScenario, duplicateScenario, renameScenario, setDefaultScenario, } from './scenarios.js';
import { addNote, annotateEvent, archiveProject, deleteView, issueSteercoPack, renameProject, saveView, setAssumption, setColumnMapping, setDefaultView, setGroupingLevels, setNodeDisplay, setPptxGeom, updateView, } from './settings.js';
import { applyDataUpdate, rebuildRosterFromSource } from './intake.js';
import { restoreProject } from './restore.js';
import { addTargetUnit, declareTargetUnits, removeTargetUnit, renameTargetValue, reparentTargetUnit, setTargetEnabled, setTargetLevels, setTargetPlacement, } from './target.js';
import { clearHead, moveGroup, renameGroup, resetRestructuring, setEmpField, setEmpFieldBulk, setHead, setLevelDisabled, } from './structure.js';
export const COMMAND_HANDLERS = {
    // Decisions
    setBucket,
    setBucketBulk,
    setJoinStage,
    setDay2Exit,
    setStrandedPlan,
    applyPerimeterThreshold,
    clearPerimeterThreshold,
    setPerimeterRule,
    setPerimeterFrozen,
    // Structure
    setHead,
    clearHead,
    renameGroup,
    moveGroup,
    setLevelDisabled,
    setEmpField,
    setEmpFieldBulk,
    resetRestructuring,
    // Registers
    addTsa,
    updateTsa,
    deleteTsa,
    addBuildUp,
    updateBuildUp,
    deleteBuildUp,
    addEntity,
    updateEntity,
    deleteEntity,
    remapEntity,
    // Scenarios
    createScenario,
    duplicateScenario,
    renameScenario,
    deleteScenario,
    setDefaultScenario,
    // Assumptions, views, display and intake configuration
    setAssumption,
    saveView,
    updateView,
    deleteView,
    setDefaultView,
    setNodeDisplay,
    setPptxGeom,
    setColumnMapping,
    setGroupingLevels,
    applyDataUpdate,
    rebuildRosterFromSource,
    // Target org design
    setTargetEnabled,
    setTargetLevels,
    addTargetUnit,
    declareTargetUnits,
    removeTargetUnit,
    renameTargetValue,
    reparentTargetUnit,
    setTargetPlacement,
    // Project and notes
    renameProject,
    archiveProject,
    addNote,
    annotateEvent,
    restoreProject,
    issueSteercoPack,
};
export const HANDLED_COMMAND_TYPES = Object.keys(COMMAND_HANDLERS);
export function handlerFor(type) {
    return COMMAND_HANDLERS[type];
}
/*
  Which commands are dangerous to run AS AN INVERSE, and why.

  This lives beside the catalogue rather than in the server, because it is a fact about the handlers: the
  server only decides what to do about it. It was a hand-written list of three in `apps/server/src/revert.ts`
  and it was missing two, which is what a hand-written list of a safety property does.

  THE CRITERION, stated so the next entry is a deduction rather than a guess: a command belongs here when
  its OWN handler can return `inverse: null`. That is what makes it unrecoverable — reverting a change runs
  this command, and the event that run produces then has no way back. Every other delete is fine however
  much it removes: `deleteBuildUp` takes the position, its decision row and every group it heads, and
  records an inverse restoring all three, so undoing the undo puts it back.

  The two that were missing are the subtle ones, because they are their own inverses and only SOMETIMES
  refuse. Rename "Ops" → "Operations"; later somebody declares a new unit called "Ops"; now undoing the
  rename runs "Operations" → "Ops", which merges into it — and a merge is permanent, because the rename
  cannot say afterwards which people belonged to which. Neither existing guard sees it: `assertNothingBuiltOnIt`
  compares `target:value:…` against the new unit's `target:unit:…` and finds no overlap.
*/
export const DESTRUCTIVE_INVERSES = {
    deleteScenario: 'it takes the whole scenario and every decision, head, override, TSA and build-up in it',
    removeTargetUnit: 'it takes the branch beneath that unit and every placement inside it',
    setTargetLevels: 'it shortens the target ladder, which drops every level below the new depth',
    renameTargetValue: 'renaming back can merge into a unit that exists now and did not then, and a merge cannot say '
        + 'afterwards which people belonged to which',
    reparentTargetUnit: 'moving back can merge into a unit that exists now, or need levels and parents that would have to '
        + 'be removed again',
};
/*
  Handlers that can also return `inverse: null` but are never STORED as another command's inverse, so
  they never reach a revert. Listed rather than omitted so the test below can account for every one of
  them, and so removing an entry from here is a deliberate act.
*/
export const NEVER_AN_INVERSE = {
    applyDataUpdate: 'nothing inverts to a roster refresh; undoing one would need a command that deletes people',
    rebuildRosterFromSource: 'nothing inverts to a re-derivation; putting the mapping back reproduces it',
    resetRestructuring: 'nothing inverts to a reset; it discards rather than records',
    restoreProject: 'nothing inverts to a restore, and nothing needs to: a point is taken immediately before this '
        + 'command runs, so the way back from a restore is another restore',
};
/*
  The third answer, and the one the classifier used to have no word for.

  A handler can return `inverse: null` and still be safe to run as an inverse, if the state it refuses in
  is a state a revert cannot put it in. That is a real category, not a hedge, and leaving it unnamed meant
  the only honest place to file `setDefaultScenario` was a list that would have been wrong either way:
  `DESTRUCTIVE_INVERSES` would gate undo on a hazard that cannot occur, and `NEVER_AN_INVERSE` is false —
  it is its own inverse.

  It refuses only when no scenario is the default yet. But the `scenarioDefault` write clears every
  default and then sets one, so after any run exactly one exists; and reverting a `setDefaultScenario`
  runs `setDefaultScenario(previous)`, which by definition happens in a project that has one. The null
  branch is reachable going forward — an imported project always has a default, but nothing in the schema
  requires it — and unreachable as an inverse. So the forward row still needs a sentence in `IRREVERSIBLE`,
  and the revert guards still need to leave it alone.

  A new entry here is a claim about reachability, and it should be argued in the string, not asserted.
*/
export const SAFE_AS_AN_INVERSE = {
    setDefaultScenario: 'it refuses only when the project has no default at all, and setting a default leaves exactly one — '
        + 'so run as an inverse, the scenario it restores the default to is always there',
};
//# sourceMappingURL=registry.js.map